import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, MenuItem, Table, TableBody, TableHead, TableRow } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomAutoComplete,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common/components';
import { useGenerateConsumptionFromOrder } from '../../../hooks/useConsumptions';
import { INITIAL_GENERATE_CONSUMPTION_STATE } from '../constants/consumptionsConstants';
import {
  buildGenerateConsumptionPayload,
  formatConsumptionQuantity,
  getConsumptionApiErrorMessage,
  getItemConsumableQuantity,
  getReadyToConsumeOrderItems,
  isPartialConsumptionScenario,
} from '../utils/consumptionsUtils';
import { useReportPendingState } from '../../hooks/useReportPendingState';
import { guardDialogClose } from '../../utils/actionLockUtils';

const GenerateConsumptionDialog = ({
  open,
  onClose,
  order,
  warehouses = [],
  defaultWarehouseId = '',
  articlesByUuid = {},
  onSuccess,
  onPendingChange,
}) => {
  const [formData, setFormData] = useState(INITIAL_GENERATE_CONSUMPTION_STATE);
  const [errorMessage, setErrorMessage] = useState('');
  const generateMutation = useGenerateConsumptionFromOrder();
  useReportPendingState(generateMutation.isPending, onPendingChange);
  const readyItems = useMemo(() => getReadyToConsumeOrderItems(order), [order]);
  const isPartial = useMemo(() => isPartialConsumptionScenario(order), [order]);

  const getArticleLabel = (articleUuid) => {
    const article = articlesByUuid[articleUuid];
    if (!article) {
      return articleUuid?.slice(0, 8) || '-';
    }

    if (article.external_code) {
      return `${article.external_code} — ${article.name}`;
    }

    return `${article.code} — ${article.name}`;
  };

  useEffect(() => {
    if (open) {
      setFormData({
        uuid_warehouse: defaultWarehouseId || order?.uuid_destination_warehouse || '',
        observations: '',
      });
      setErrorMessage('');
    }
  }, [open, order?.uuid, defaultWarehouseId, order?.uuid_destination_warehouse]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      const payload = buildGenerateConsumptionPayload(formData);
      const consumption = await generateMutation.mutateAsync({
        orderId: order.uuid,
        payload,
      });
      onSuccess?.(consumption);
      onClose();
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo generar el consumo')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(generateMutation.isPending, onClose)}
      size="medium"
    >
      <DialogTitle>
        {isPartial ? 'Consumir materiales listos' : 'Generar consumo'}
      </DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <CustomTextField
          label="Orden técnica"
          name="orderNumber"
          value={order?.number || '-'}
          disabled
        />

        {readyItems.length > 0 && (
          <Box>
            <Box component="p" sx={{ m: 0, mb: 1, fontWeight: 600 }}>
              Materiales a consumir
            </Box>
            <GeneralTableBox>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell>Artículo</HeadTableCell>
                    <HeadTableCell>Solicitado</HeadTableCell>
                    <HeadTableCell>Faltante</HeadTableCell>
                    <HeadTableCell>A consumir</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {readyItems.map((item) => (
                    <TableRow key={item.uuid}>
                      <RowTableCell>
                        {getArticleLabel(item.uuid_inventory_article)}
                      </RowTableCell>
                      <RowTableCell>{item.requested_quantity}</RowTableCell>
                      <RowTableCell>{item.missing_quantity}</RowTableCell>
                      <RowTableCell>
                        {formatConsumptionQuantity(getItemConsumableQuantity(item))}
                      </RowTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GeneralTableBox>
          </Box>
        )}

        <CustomAutoComplete
          label="Depósito"
          name="uuid_warehouse"
          value={formData.uuid_warehouse}
          onChange={handleChange}
        >
          {warehouses.map((warehouse) => (
            <MenuItem key={warehouse.uuid} value={warehouse.uuid}>
              {warehouse.name || warehouse.code}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomTextField
          label="Observaciones"
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          multiline
          minRows={2}
        />

        <Box component="p" sx={{ m: 0, color: 'text.secondary', fontSize: 14 }}>
          {isPartial
            ? 'Se consumirá la cantidad ya disponible de cada material. El faltante seguirá pendiente hasta completar traslado o recepción.'
            : 'Al generar el consumo se descontará el stock del depósito seleccionado. Los artículos y cantidades provienen del pedido técnico y no pueden editarse.'}
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel={isPartial ? 'Consumir materiales listos' : 'Generar consumo'}
        saveButtonProps={{ isLoading: generateMutation.isPending }}
      />
    </DialogContainer>
  );
};

export default GenerateConsumptionDialog;
